import { useContext, useRef, useState } from 'react'
import axios from 'axios'
import { observer } from 'mobx-react'
import { StoreContext } from '@store/Root.store'
import { Header, OtpInput, Button } from '../../Generic'

import '../ResumeFromEmail.css'
import { devLog } from '@methods/devLog'
import { useInterval } from 'usehooks-ts'
import { useKeyboardAwareViewport } from '@/hooks/useKeyboardAwareViewport'
import '@/components/Generic/OtpInput/OtpInput.css'
import { cx } from '@/utils/cx'

interface OtpProps {
  maxAttempts: number
  attemptsLeft: number
  formActionUrl: string
  isSmsEnabled?: boolean
  sendOtpUrl?: string
  smsMaxAttempts: number
  smsRetrySeconds?: number
  otpAttemptsLeft?: number
}

export const Otp = observer(
  ({
    maxAttempts,
    attemptsLeft,
    formActionUrl,
    isSmsEnabled,
    sendOtpUrl,
    smsMaxAttempts,
    smsRetrySeconds,
  }: OtpProps) => {
    const viewport = useKeyboardAwareViewport()

    const store = useContext(StoreContext)
    const { isMobile } = store.AppState
    const { ResumeFromEmailPage: trans } = store.TranslationsState.translations

    const formRef = useRef<HTMLFormElement>(null)

    const [isCodeBeingChecked, setIsCodeBeingChecked] = useState(false)
    const [isCodeIncorrect, setIsCodeIncorrect] = useState(!!attemptsLeft)

    const [disabled, setDisabled] = useState(false)
    const [isSendSmsButtonVisible, setIsSendSmsButtonVisible] = useState(false)
    const [sendSmsCount, setSendSmsCount] = useState(1)
    const [counting, setCounting] = useState(true)
    const [countdownProgress, setCountdownProgress] = useState(smsRetrySeconds ?? 0)

    const [otp, setOtp] = useState('')

    const handleTimeCount = () => {
      if (countdownProgress > 0) {
        setCountdownProgress(countdownProgress - 1)
      } else {
        setCountdownProgress(smsRetrySeconds ?? 0)
        setCounting(false)
        setIsSendSmsButtonVisible(true)
        setDisabled(false)
      }
    }

    const sendSms = async () => {
      if (!isSmsEnabled || !sendOtpUrl) return
      setSendSmsCount(sendSmsCount + 1)
      devLog('smsUrl', sendOtpUrl)
      setIsSendSmsButtonVisible(false)
      setCounting(true)
      setDisabled(true)

      try {
        axios.post(
          sendOtpUrl,
          {},
          {
            withCredentials: true,
          }
        )
      } catch (error) {
        devLog(error)
      }
    }

    const renderSendSmsStatus = () => (
      <div className="resume-from-email__centered-sms-text">
        {!isSendSmsButtonVisible && <p>{trans.OtpPage.smsSent}</p>}

        <Button
          disabled={disabled || !isSendSmsButtonVisible || sendSmsCount >= smsMaxAttempts}
          onClick={sendSms}
          className="button--secondary"
        >
          <span className="generic__button-text">{trans.OtpPage.sendSmsAgainBtn}</span>
        </Button>

        {!isSendSmsButtonVisible && sendSmsCount < smsMaxAttempts && (
          <>
            <p>{trans.OtpPage.smsNotReceived}</p>

            <p>{trans.OtpPage.sendSmsAgain.replace('<number>', countdownProgress.toString())}</p>
          </>
        )}
      </div>
    )

    useInterval(handleTimeCount, counting ? 1000 : null)

    const onClickHandler = () => {
      setIsCodeBeingChecked(true)
      if (formRef.current) {
        formRef.current.submit()
      }
    }

    return (
      <form ref={formRef} action={formActionUrl} method="post">
        <Header className="m-30-0">{trans.OtpPage.header}</Header>

        <p className="generic__intro-paragraph centered">{trans.OtpPage.description}</p>

        <div className="layout__margined m-0-0-30" />

        <div
          className={cx(
            'otp-input__code-input-wrapper',
            isCodeIncorrect && 'otp-input__code-input-wrapper--error'
          )}
        >
          <OtpInput
            value={otp}
            valueLength={6}
            onChange={(val) => {
              setOtp(val)
              setIsCodeIncorrect(false)
            }}
            className="otp-input__container--h-68"
          />

          {isCodeIncorrect && <p className="generic__intro-paragraph"> {trans.OtpPage.error1}</p>}

          {attemptsLeft < maxAttempts && attemptsLeft > 1 && (
            <span>{trans.OtpPage.attemptsLeft.replace('<number>', attemptsLeft.toString())}</span>
          )}

          {attemptsLeft === 1 && <span>{trans.OtpPage.lastAttempt}</span>}
        </div>

        <input hidden name="otp" value={otp}></input>

        {isSmsEnabled && <>{renderSendSmsStatus()}</>}

        <div
          className="layout__content-container-bottom-fixed"
          style={
            viewport?.keyboardHeight
              ? { top: 'unset', bottom: `${viewport.keyboardHeight}px` }
              : undefined
          }
        >
          {!isMobile ? <div className="layout__content-container-bottom-left" /> : <></>}
          <Button
            disabled={isCodeBeingChecked || otp.length < 6}
            type="submit"
            className="button--w190 resume-from-email__otp-continue-btn"
            onClick={onClickHandler}
          >
            <span className="generic__button-text">{trans.OtpPage.continue}</span>
          </Button>
        </div>

        <div className="layout__mobile-spacer-for-content-container-bottom-fixed" />
      </form>
    )
  }
)
